import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import OverallWidget from './OverallWidget';
import TopReviewsWidget from './TopReviewsWidget';
import ReviewsByPlatformWidget from './ReviewsByPlatformWidget';
import LeaveReviews from './LeaveReviews';
import ReviewsList from './ReviewsList';
import * as T from './types';
import * as S from './styles';
import { TCButton } from 'components/TCButton';
import { TC_BUTTON_TYPE } from 'components/TCButton/constants';
import ScriptIcon from 'common/icons/scriptIcon';
import EditStylesIcon from 'common/icons/editStylesIcon';
import { RootState } from 'store/types';
import { getProvidersRating } from '../state/actions';
import TextToolTip from 'components/TextToolTip';
import SelectProviders from './SelectProviders';
import CopyCodePanel from './CopyCodePanel';
import SearchReviews from './SearchReviews';
import StylesPanel from './StylesPanel';
import { createRGBA, getReviewsConfig } from '../state/utils';
import { Context } from '../context';

const Widget: React.FC<T.IProps> = (props) => {
    const context = React.useContext(Context);

    const [showProvidersSelector, setShowProvidersSelector] = React.useState<T.IShowProvider>({ show: false });
    const [showSearchReviews, setShowSearchReviews] = React.useState<T.IShowSearchReviews>({ show: false });
    const [showStylesPanel, setShowStylesPanel] = React.useState<T.IShowStylesPanel>({ show: false });
    const [exportCodeId, setExportCodeId] = React.useState<string | undefined>();
    const [openExportCode, setOpenExportCode] = React.useState<boolean>(false);
    const [exportCodeXPosition, setExportCodeXPosition] = React.useState<number>(0);
    const [exportCodeYPosition, setExportCodeYPosition] = React.useState<number>(0);

    const dispatch = useDispatch();

    const {
        selectedDealerId,
        reviewsConfig,
    } = useSelector((rootState: RootState) => {
        return {
            selectedDealerId: rootState.dealers.dealer.selectedDealer?.id,
            reviewsConfig: rootState.reviews.reviewsConfig,
        };
    }, shallowEqual);

    React.useEffect(() => {
        if (!reviewsConfig) getReviewsConfig(dispatch);
    }, []);

    React.useEffect(() => {
        if (selectedDealerId) {
            dispatch(getProvidersRating(selectedDealerId));
        }
    }, [selectedDealerId]);

    function handleExportCodeClick(event, id: string) {
        setExportCodeXPosition(event.clientX);
        let element: any = event.target;
        let offsetTop = 0;
        while (element && !(element.id as string).startsWith('container')) {
            offsetTop = element.offsetTop;
            element = element.parentElement;
        }
        if (!element) {
            offsetTop = 0;
        }
        setExportCodeYPosition(offsetTop + 240);
        if (!openExportCode) {
            setExportCodeId(id);
            setOpenExportCode(true);
        }
    }

    function handleEditStyles(event: React.MouseEvent<HTMLDivElement>, id: string, text: string) {
        let element: any = event.target;
        let offsetTop = 0;
        while (element && !(element.id as string).startsWith('container')) {
            offsetTop = element.offsetTop;
            element = element.parentElement;
        }
        if (!element) {
            offsetTop = 0;
        }
        setShowStylesPanel({
            show: true,
            posX: event.pageX,
            posY: offsetTop,
            selectedWidget: {id, label: text},
        })
    }

    const closeStylesPanel = () => {
        setShowStylesPanel({show: false})
        context.handleEditingStatus({});
    }

    const renderProvidersSelector = (render: boolean) =>{
        return (render && showProvidersSelector.show) ? (
            <SelectProviders
            selectedTab={props.selectedTab}
            widgetsConfig={props.widgetsConfig}
            onClose={() => setShowProvidersSelector({show: false})}
            handleTotalSelected={(totalSelected: number) => setShowProvidersSelector({...showProvidersSelector,totalSelected})}
            {...showProvidersSelector}
          />
        ):'';
    }

    const renderWidget = (child: JSX.Element, id: string, leftText: string) => {
        return (
            <S.WidgetContainer
                hasMargin={props.selectedTab === "individual"}
                verticalMargin={props.selectedTab === 'individual' ? 26 : 0}
                backgroundColor={context.editingStatus.background?.id === id ? createRGBA(context.editingStatus.background.color, context.editingStatus.background.opacity) : createRGBA(props.widgetsConfig[id].backgroundColor, props.widgetsConfig[id].backgroundOpacity)}
            >
                {props.selectedTab === 'individual' && (
                    <S.LeftText>{leftText}</S.LeftText>
                )}
                {child}                
                <TextToolTip
                    tip="Design"
                    style={{position: "absolute", top: "23px", right: "97px"}}
                >
                    <S.ExportCodeContainer onClick={(event) => handleEditStyles(event, id, leftText)}>
                        <EditStylesIcon />
                    </S.ExportCodeContainer>
                </TextToolTip>
                {(props.selectedTab === 'individual' || id === 'overallRating') && (
                    <TextToolTip
                        tip="Export"
                        style={{position: "absolute", top: "23px", right: "23px"}}
                    >
                        <S.ExportCodeContainer
                            onClick={(event) => handleExportCodeClick(event, id)}
                        >
                            <ScriptIcon />
                        </S.ExportCodeContainer>
                    </TextToolTip>
                )}
            </S.WidgetContainer>
        );
    };

    return (
      <S.Root>
      {props.selectedTab === 'entire' && (
        <S.SelectProviders>
            <TCButton
                type={TC_BUTTON_TYPE.PRIMARY}
                onClick={() => setShowProvidersSelector({ show: true })}
            >
                Select Providers
            </TCButton>
        </S.SelectProviders>
      )}
        <S.MainContainer
            id="container"
            hasMargin={props.selectedTab === "entire"}
        >
            {renderWidget(
                <OverallWidget
                    {...props}
                    handleEditProviders={() => setShowProvidersSelector({ show: true })}
                />,
                'overallRating',
                'Aggregate reviews',
            )}
            {renderWidget(
                <ReviewsByPlatformWidget
                    {...props}
                    handleEditProviders={(values: any) =>
                        setShowProvidersSelector(values)
                    }
                    totalSelected={showProvidersSelector.totalSelected}
                />,
                'providersRating',
                'Reviews by Platform',
            )}
            {renderWidget(
                <TopReviewsWidget
                    {...props}
                    handleShowSearchReviews={(values: T.IShowSearchReviews) => setShowSearchReviews(values)}
                />,
                "topReviews",
                "Featured reviews",
            )}
            {renderWidget(
                <LeaveReviews
                    {...props}
                    handleEditProviders={(values: any) =>
                        setShowProvidersSelector(values)
                    }
                />,
                'leaveReview',
                'Leave a Review',
            )}
            {renderWidget(
                <ReviewsList
                    {...props}
                    handleEditProviders={(values: any) =>
                        setShowProvidersSelector(values)
                    }
                />,
                'reviewList',
                'Reviews List',
            )}
            {(showStylesPanel.show && showStylesPanel.selectedWidget) &&
                <StylesPanel
                    {...props}
                    posX={showStylesPanel.posX ?? 0}
                    posY={showStylesPanel.posY ?? 0}
                    selectedWidget={showStylesPanel.selectedWidget}
                    onClose={closeStylesPanel}
                />
            }
            {showSearchReviews.show &&
                <SearchReviews
                    {...props}
                    posX={showSearchReviews.posX ?? 0}
                    posY={showSearchReviews.posY ?? 0}
                    onClose={() => setShowSearchReviews({show: false})}
                />
            }
            {openExportCode && (
                <CopyCodePanel
                    selectedTab={props.selectedTab}
                    widgetType={exportCodeId ?? ''}
                    xPosition={exportCodeXPosition}
                    yPosition={exportCodeYPosition}
                    setOpen={setOpenExportCode}
                ></CopyCodePanel>
            )}
            {renderProvidersSelector(props.selectedTab==='individual')}
        </S.MainContainer>
        {renderProvidersSelector(props.selectedTab==='entire')}
      </S.Root>
    );
};

export default Widget;
